import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';

import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { GalaxyTableModule } from '@vendasta/galaxy/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TemplateService } from '../../../../../core/src/lib/template.service';
import { TextMessageBuilderComponent, TextMessageBuilderForm } from '@galaxy/sms';
import { MessageTemplate } from '@vendasta/conversation';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { firstValueFrom, Observable } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'inbox-edit-message-template',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    GalaxyPageModule,
    TranslateModule,
    MatIconModule,
    MatButtonModule,
    RouterLink,
    MatListModule,
    MatMenuModule,
    MatSortModule,
    MatTableModule,
    GalaxyTableModule,
    MatTooltipModule,
    TextMessageBuilderComponent,
  ],
  templateUrl: './edit-message-template.component.html',
})
export class EditMessageTemplateComponent implements OnInit {
  private readonly templateService = inject(TemplateService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly route = inject(ActivatedRoute);

  originalTemplate$: Observable<MessageTemplate>;
  inputMessageBuilderText$: Observable<TextMessageBuilderForm>;

  outputMessageBuilderText?: TextMessageBuilderForm;
  isValid = false;

  ngOnInit(): void {
    this.originalTemplate$ = this.route.params.pipe(
      switchMap((params) => this.templateService.getTemplate(decodeURIComponent(params['templateId']))),
      map((getTemplateRes) => {
        if (!getTemplateRes) {
          this.back();
          return;
        }
        return getTemplateRes?.template;
      }),
      distinctUntilChanged(),
    );

    this.inputMessageBuilderText$ = this.originalTemplate$.pipe(
      map((template) => {
        return {
          name: template?.name,
          message: template?.content,
        };
      }),
    );
  }

  back(): void {
    window.history.back();
  }

  async edit(): Promise<void> {
    // If the form is invalid, we will display error message
    if (!this.isValid) {
      this.snackbarService.openErrorSnack('INBOX.SETTINGS.MESSAGE_TEMPLATES.TEMPLATE_MISSING_FIELDS');
      return;
    }

    const originalTemplate = await firstValueFrom(this.originalTemplate$);
    const template = new MessageTemplate({
      templateId: originalTemplate.templateId,
      name: this.outputMessageBuilderText.name,
      content: this.outputMessageBuilderText.message,
    });

    this.templateService
      .updateTemplate(template)
      .then(() => {
        this.snackbarService.openSuccessSnack('INBOX.SETTINGS.MESSAGE_TEMPLATES.UPDATE_TEMPLATE_SUCCESSFUL');
        this.back();
      })
      .catch((error) => {
        this.snackbarService.openErrorSnack('INBOX.SETTINGS.MESSAGE_TEMPLATES.UPDATE_TEMPLATE_FAILED');
        console.error(error);
      });
  }
}
